import React from 'react';

import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { Text } from '../../../components';
import { PlaceSelector, MachineSelector, RouteSelector, CommDeviceSelector } from '../../../components/selector';
import SetupSection from '../components/SetupSection';
import SetupInfoRow from '../components/SetupInfoRow';
import SetupTextField from '../components/SetupTextField';
import SetupPhotoSection from '../components/SetupPhotoSection';
import SetupMultiSelector from '../components/SetupMultiSelector';
import SetupAssigneePicker from '../components/SetupAssigneePicker';
import { flattenObjectDataInArray } from '../../../../common/utils/general/flattenObjectData';
import { POS_TYPE } from '../../../../common/constants';
import { createMarketSetupDetailStyles } from './MarketSetupDetail.styles';

const MarketSetupInformationSection = ({
	marketSetup,
	assignedMarket,
	locationSetupLabel,
	dueDateLabel,
	updateMarketSetup,
	addPhoto,
	handleUpdateMediaComment,
}) => {
	const styles = useThemedStyles(createMarketSetupDetailStyles);
	const renderFieldLabel = (labelText, required = false) => (
		<Text style={styles.fieldLabel}>
			{labelText}
			{required && <Text style={styles.requiredAsterisk}> *</Text>}
		</Text>
	);

	return (
		<SetupSection title="Information">
			<Text style={styles.sectionLabel}>Basic information</Text>
			<SetupInfoRow label="Identifier" value={marketSetup.identifier} />
			<SetupInfoRow label="Location setup" value={locationSetupLabel} />
			<SetupInfoRow label="Due date" value={dueDateLabel} />
			<SetupInfoRow label="Location" value={marketSetup.locationSetup?.location?.name || 'Unknown location'} />

			{renderFieldLabel('Place')}
			<PlaceSelector
				initialValue={marketSetup.place}
				value={marketSetup.place}
				onChange={place => updateMarketSetup({ place: place?.id || null })}
				location={marketSetup.locationSetup?.location}
				disabled={!assignedMarket}
				buttonStyle={styles.selectorButton}
			/>
			<Text style={styles.helperText}>Insert the machine place, e.g. &apos;Lunchroom&apos; or &apos;Warehouse&apos;, if known.</Text>
			{
				!assignedMarket && (
					<Text style={styles.helperText}>To select the place, first select the market.</Text>
				)
			}

			{renderFieldLabel('Market', true)}
			<MachineSelector
				initialValue={assignedMarket}
				value={assignedMarket}
				onChange={machine => updateMarketSetup({ machine: machine?.id || null })}
				initialFilter={{ type: POS_TYPE.MARKET }}
				hideRemoveButton={true}
				disabled={Boolean(marketSetup.movePlanId)}
				buttonStyle={styles.selectorButton}
			/>

			{renderFieldLabel('Route', true)}
			<RouteSelector
				initialValue={marketSetup.route}
				value={marketSetup.route}
				onChange={route => updateMarketSetup({ route: route?.id || null })}
				buttonStyle={styles.selectorButton}
			/>
			<Text style={styles.helperText}>Route will be assigned to market automatically.</Text>

			{renderFieldLabel('Kiosk(s)')}
			<SetupMultiSelector
				items={marketSetup.commDevices || []}
				onChange={ids => updateMarketSetup({ commDevices: flattenObjectDataInArray(ids) })}
				SelectorComponent={CommDeviceSelector}
				placeholder="Add kiosk"
				disabled={!assignedMarket}
				selectorProps={{ buttonStyle: styles.selectorButton }}
			/>
			{
				!assignedMarket && (
					<Text style={styles.helperText}>To select the comm device(s), first select the market.</Text>
				)
			}

			<Text style={styles.sectionLabel}>Assignment</Text>
			<SetupAssigneePicker
				assignees={marketSetup.assignees || []}
				onChange={assignees => updateMarketSetup({ assignees })}
				placeholder="Add assignee"
				initialFilter={{
					enabled: true,
					marketSetupAssigneeEligible: true,
				}}
			/>

			<Text style={styles.sectionLabel}>Other information</Text>
			<SetupTextField
				label="Additional instructions"
				value={marketSetup.additionalInstructions}
				field="additionalInstructions"
				onConfirm={updateMarketSetup}
				multiline={true}
			/>

			<SetupPhotoSection
				title="Site photos"
				photos={marketSetup.placePhotos || []}
				onAddPhoto={(link) => addPhoto({ link, targetField: 'placePhotos' })}
				onUpdateComment={handleUpdateMediaComment}
				addButtonLabel="Add site photo"
			/>
		</SetupSection>
	);
};

export default MarketSetupInformationSection;
